import { ENDPOINTS } from '../consts/endpoints';
import { AbstractApi } from '../../abstract-api';
import {
  GetRecommendationsRespBody,
  PutUserResourceInteractionBody,
  PutUserResourceInteractionRespBody,
  HideRecommendationRespBody
} from '../schema';
import {
  Resource,
  PutResourceInput,
  PostResourceInput,
  PatchResourceStatusInput,
  ResourceManagementResponse,
} from '../types/resource-management.types';
import {
  ResourceFacets,
  GetResourcesParams,
  PostManagerEmailBody,
  ResourceRecommendations,
  Resource as ResourceType,
  GetResourcesPaginatedParamsExtended,
  PostHideRecommendationBody,
} from '../types';

export class ResourceApi extends AbstractApi {
  async getResources(
    params: GetResourcesParams = {
      pageNumber: 0,
      pageSize: 0,
    }
  ): Promise<ResourceType[]> {
    const axios = await this.axios();
    const safeParams = {
      mediaTypes: params.mediaType,
      searchQuery: params.searchQuery,
      skillCategories: params.skillCategories,
    };
    const { data } = await axios.get<{ resources: ResourceType[] }>(
      ENDPOINTS.LEARNING.RESOURCE.BASE,
      {
        params: safeParams,
      }
    );
    return data.resources;
  }

  async getResourcesPaginated(
    params: GetResourcesPaginatedParamsExtended = {
      pageNumber: 0,
      pageSize: 0,
    }
  ): Promise<{ resources: ResourceType[]; totalCount: number }> {
    const axios = await this.axios();
    const safeParams = {
      mediaTypes: params.mediaTypes,
      searchQuery: params.searchQuery,
      skillCategories: params.skillCategories,
      isInternal: params.isInternal,
      status: params.status,
      updatedFrom: params.updatedFrom,
      updatedTo: params.updatedTo,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    };
    const { data } = await axios.get<{ resources: ResourceType[]; totalCount: number }>(
      ENDPOINTS.LEARNING.RESOURCE.PAGINATED,
      {
        params: safeParams,
      }
    );
    return {
      resources: data.resources,
      totalCount: data.totalCount,
    };
  }

  async getResourcesForManager(
    params: GetResourcesPaginatedParamsExtended = {
      pageNumber: 0,
      pageSize: 0,
    }
  ): Promise<{ resources: Resource[]; totalCount: number }> {
    const axios = await this.axios();
    const safeParams = {
      mediaTypes: params.mediaTypes,
      searchQuery: params.searchQuery,
      skillCategories: params.skillCategories,
      isInternal: params.isInternal,
      status: params.status === '' ? undefined : params.status,
      updatedFrom: params.updatedFrom === '' ? undefined : params.updatedFrom,
      updatedTo: params.updatedTo === '' ? undefined : params.updatedTo,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    };
    const { data } = await axios.get<ResourceManagementResponse>(ENDPOINTS.LEARNING.MANAGE.BASE, {
      params: safeParams,
    });

    return {
      resources: data.resources.rows,
      totalCount: data.resources.totalCount,
    };
  }

  async postResource(payload: PostResourceInput): Promise<Resource> {
    const axios = await this.axios();
    const { data } = await axios.post<Resource>(ENDPOINTS.LEARNING.MANAGE.RESOURCE, payload);
    return data;
  }

  async putResource(resourceId: string, payload: PutResourceInput): Promise<Resource> {
    const axios = await this.axios();
    const { data } = await axios.put<Resource>(
      `${ENDPOINTS.LEARNING.MANAGE.RESOURCE}/${resourceId}`,
      payload
    );
    return data;
  }

  async patchResourceStatus(
    resourceId: string,
    status: PatchResourceStatusInput
  ): Promise<Resource> {
    const axios = await this.axios();
    const { data } = await axios.patch<Resource>(
      `${ENDPOINTS.LEARNING.MANAGE.RESOURCE}/${resourceId}/status`,
      { status }
    );
    return data;
  }

  // Not used, will be used once we implement appropriate pagination
  // async getSearch(params: GetResourcesSearchParams = {}): Promise<ResourceSearch> {
  //   const axios = await this.axios();
  //   const safeParams = {
  //     searchQuery: params.searchQuery,
  //     mediaTypes: params.mediaTypes,
  //     skillCategories: params.skillCategories,
  //     pageNumber: params.pageNumber,
  //     pageSize: params.pageSize,
  //   };
  //   const { data } = await axios.get<GetResourceSearchRespBody>('/learning/resource-search', {
  //     params: safeParams,
  //   });
  //   return data;
  // }

  async getFacets(): Promise<ResourceFacets> {
    const axios = await this.axios();
    const { data } = await axios.get<ResourceFacets>(ENDPOINTS.LEARNING.RESOURCE.FACETS);
    return data;
  }

  async getRecommendations(): Promise<ResourceRecommendations[]> {
    const axios = await this.axios();
    const { data } = await axios.get<GetRecommendationsRespBody>(
      ENDPOINTS.LEARNING.RESOURCE.RECOMMENDATIONS
    );
    return data.resources;
  }

  async putUserResourceInteraction(
    data: PutUserResourceInteractionBody
  ): Promise<PutUserResourceInteractionRespBody> {
    const axios = await this.axios();
    const response = await axios.put(ENDPOINTS.LEARNING.RESOURCE.INTERACTION, data);
    return response.data;
  }

  async emailManager(payload: PostManagerEmailBody) {
    const axios = await this.axios();
    const { data } = await axios.post(ENDPOINTS.LEARNING.RESOURCE.EMAIL_MANAGER, payload);
    return data;
  }

  async postHideRecommendation(
    payload: PostHideRecommendationBody
  ): Promise<HideRecommendationRespBody> {
    const axios = await this.axios();
    const { data } = await axios.post(
      `${ENDPOINTS.LEARNING.RESOURCE.RECOMMENDATION}/${payload.recommendationId}/hide`, 
        payload
    );
    return data;
  }
}
